import {
  Box,
  HStack,
  Icon,
  Stack,
  StackProps,
  Text,
  useBreakpointValue,
  useConst,
  VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import React from 'react';
import { FiCheck } from 'react-icons/fi';

import { TypeUtils } from '../../../../../../framework/utils/UtilityTypes';
import ClickableText from '../../../../../components/ClickableText';
import { MasterKey } from '../../../../../models/gen/masterKey';
import { Formatter } from '../../../../../utils/FormatUtils';
import { useMasterContext } from '../../../../MasterProvider';
import { useSizeContext } from '../../../../SizeProvider';
import {
  OperationButtonAreaProps,
  useOperationButtonArea,
} from './OperationButtonsArea';

/**
 * 日次業務アイテムプロパティ基底
 */
export type DailyMatterItemBase = StackProps &
  OperationButtonAreaProps & {
    attendanceTypeId?: number;
    /**
     * 日付文字列
     */
    dateString: string;
    /**
     * 現場名クリックイベント
     */
    onClickMatterName?: Runnable;
  };

/**
 * 日次業務プロパティ
 */
type DailyMatterItemProps = DailyMatterItemBase & {
  /**
   * 依頼元
   */
  customerName?: string;
  /**
   * 編集ボタンの無効フラグ
   */
  disabledButton: boolean;
  /**
   * 現場名
   */
  matterName?: string;
  /**
   * 作業内容エリア
   */
  workContents: React.FunctionComponent;
};

/**
 * 日次業務アイテム
 * @param props - プロパティ
 * @returns
 */
const DailyMatterItem: React.FunctionComponent<DailyMatterItemProps> = ({
  attendanceTypeId,
  customerName,
  dateString,
  isApplied,
  disabledButton,
  isOutsourcer,
  matterName,
  onClickMatterName,
  workContents: WorkContents,
  onClickMatterExpense,
  onClickWorkerAssignment,
  onClickWorkContent,
  onClickAssignment,
  onClickWorkReport,
  ...props
}) => {
  const { OperationButtonArea, areaWidth: buttonAreaWidth } =
    useOperationButtonArea();
  const sizes = useSizeContext();
  const editIconWidthPx = useConst(26);
  const dateWidthPx = useBreakpointValue({ base: 60, md: 110 });
  const master = useMasterContext();
  // const isMobile = useBreakpointValue({ base: true, md: false });

  const date = Formatter.createDate(dateString);

  return (
    <Stack
      flexDirection={{ base: 'column', md: 'row' }}
      justifyContent="space-between"
      width="100%"
      {...props}
    >
      <Stack
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        minWidth={{ base: 'full', md: `calc(95% - ${buttonAreaWidth}px)` }}
      >
        <Stack
          flexDirection={{ base: 'column', md: 'row' }}
          minWidth={{ base: 'full', md: '48%' }}
        >
          <HStack>
            <Text minWidth={`${dateWidthPx}px`}>
              {Formatter.dateOfWeek(date)}
            </Text>
            <Box minWidth={`${editIconWidthPx}px`}>
              {isApplied ? (
                <Icon
                  as={FiCheck}
                  color="blue.500"
                  fontSize={sizes.fontSize.iconSize}
                  marginTop="2px"
                  size="ms"
                />
              ) : null}
            </Box>
          </HStack>
          <VStack
            alignItems="start"
            width={{
              base: 'full',
              md:
                dateWidthPx !== undefined
                  ? `calc(95% - ${dateWidthPx + editIconWidthPx}px)`
                  : '90%',
            }}
          >
            <ClickableText
              hasLink={!TypeUtils.isNilString(matterName)}
              marginLeft={2}
              onClick={
                TypeUtils.isNilString(matterName) ||
                TypeUtils.isNil(onClickMatterName)
                  ? () => null
                  : onClickMatterName
              }
            >
              {matterName ||
                (TypeUtils.isNonNilNumber(attendanceTypeId)
                  ? master.getBy(MasterKey.AttendanceTypes, attendanceTypeId)
                      ?.name || '―'
                  : '未定')}
            </ClickableText>
            <Text hidden={TypeUtils.isNilString(customerName)} paddingLeft={2}>
              依頼元: {customerName}
            </Text>
          </VStack>
        </Stack>
        <HStack
          flexWrap="wrap"
          fontSize={{ base: 'md' }} // { base: 'mobile.xs', md: 'md' }
          justifyContent="left"
          width={{ base: 'full', md: '48%' }}
        >
          <WorkContents />
        </HStack>
      </Stack>
      <OperationButtonArea
        // hidden={isMobile}
        isApplied={!!isApplied}
        isOutsourcer={isOutsourcer}
        onClickAssignment={onClickAssignment}
        onClickMatterExpense={onClickMatterExpense}
        onClickWorkContent={onClickWorkContent}
        onClickWorkReport={onClickWorkReport}
        onClickWorkerAssignment={onClickWorkerAssignment}
      />
    </Stack>
  );

  // return (
  //   <VStack width="100%">
  //     <HStack width="100%" justifyContent="space-between" flexWrap="wrap">
  //       <HStack width={`calc(95% - ${buttonAreaWidth}px)`} flexWrap="wrap">
  //         <HStack minWidth="48%">
  //           <Text minWidth={`${dateWidthPx}px`}>
  //             {format(date, 'MM/dd (ccccc)', { locale: ja })}
  //           </Text>
  //           <IconButton
  //             aria-label="edit"
  //             as={isApplied ? FiCheck : FiEdit3}
  //             fontSize={sizes.fontSize.iconSize}
  //             minWidth={`${editIconWidthPx}px`}
  //             color="blue.500"
  //             size="ms"
  //             disabled={props.disabledButton}
  //             onClick={props.onOpenContent}
  //           />
  //           <Text
  //             textAlign="left"
  //             width={
  //               dateWidthPx !== undefined
  //                 ? `calc(95% - ${dateWidthPx + editIconWidthPx}px)`
  //                 : '90%'
  //             }
  //           >
  //             {props.matterName}
  //           </Text>
  //         </HStack>
  //         <HStack
  //           width="48%"
  //           flexWrap="wrap"
  //           fontSize={{ base: 'mobile.xs', md: 'md' }}
  //           justifyContent="left"
  //         >
  //           <WorkContents />
  //         </HStack>
  //       </HStack>
  //       <RightButtonArea
  //         hidden={isMobile}
  //         isApplied={isApplied}
  //         onOpenAssign={props.onOpenAssign}
  //         onOpenExpense={props.onOpenExpense}
  //         onOpenOperation={props.onOpenOperation}
  //         onOpenReport={props.onOpenReport}
  //         onOpenContent={props.onOpenContent}
  //       />
  //     </HStack>
  //     <RightButtonArea
  //       hidden={!isMobile}
  //       isApplied={isApplied}
  //       onOpenAssign={props.onOpenAssign}
  //       onOpenExpense={props.onOpenExpense}
  //       onOpenOperation={props.onOpenOperation}
  //       onOpenReport={props.onOpenReport}
  //       onOpenContent={props.onOpenContent}
  //     />
  //   </VStack>
  // );
};

export default DailyMatterItem;
